import { Group, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCreatePage, usePages, usePageTemplates, useUpdatePage } from '../../api/pages-client/pages-client.hooks';
import { Page, PageTemplateInput, PageWritePayload } from '../../api/pages-client/pages-client.type';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useDashboardsStore } from '../../store/dashboards-store/dashboards-store';
import { setSelectedTab } from '../../store/dashboards-store/dashboards-store.actions';
import { DashboardTab } from '../../store/dashboards-store/dashboards-store.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import { baseWhite, inkLight } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { H2 } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { EditDashboardModal } from './components/dashboard-modal';
import { DashboardTemplateList } from './components/dashboard-template-list/dashboard-template-list';
import { DashboardsList } from './components/dashboards-list/dashboards-list';
import { EmptyStateBox } from './components/empty-state-box';

export const Dashboards = () => {
  const i18n = useTranslation();
  useDocumentTitle('Dashboards - Bloomfilter');
  const navigate = useNavigate();
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedTab = useDashboardsStore((state) => state.selectedTab);

  const [selectedDashboard, setSelectedDashboard] = useState<Page | null>(null);
  const [isCreateDashboardOpen, setIsCreateDashboardOpen] = useState(false);
  const [isEditDashboardOpen, setIsEditDashboardOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { pages, query: pagesQuery } = usePages(
    organization?.id || '',
    portfolio?.id || '',
    'dashboard', // type
    'dashboard', // moduleType
  );
  const { pageTemplates, query: pageTemplatesQuery } = usePageTemplates(organization?.id || '', portfolio?.id);

  // Filter dashboards and templates based on search query
  const filteredDashboards = useMemo(() => {
    if (!searchQuery.trim()) return pages;
    return pages.filter(
      (page) =>
        page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (page.description && page.description.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [pages, searchQuery]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return pageTemplates;
    return pageTemplates.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [pageTemplates, searchQuery]);

  const createPageMutation = useCreatePage();
  const updatePageMutation = useUpdatePage();

  const onCreateDashboard = async (data: {
    name: string;
    description: string;
    pageTemplateId: string | null;
    pageTemplateInputs: PageTemplateInput[];
  }) => {
    if (!organization?.id) return;

    try {
      const newPage: PageWritePayload = {
        name: data.name,
        description: data.description,
        type: 'dashboard',
        layout: 'grid',
        organization: organization.id || '',
        portfolio: portfolio?.id || '',
        module_type: 'dashboard',
        page_template: data.pageTemplateId,
        page_template_inputs: data.pageTemplateInputs.reduce(
          (acc, input) => {
            acc[input.key] = input.id || '';
            return acc;
          },
          {} as Record<string, string>,
        ),
      };

      // Add organization_id to the request payload
      const createdPage = await createPageMutation.mutateAsync({
        ...newPage,
      });
      setIsCreateDashboardOpen(false);
      navigate(`/application/dashboards/${createdPage.id}`);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditDashboard = (dashboard: Page) => {
    setSelectedDashboard(dashboard);
    setIsEditDashboardOpen(true);
  };

  const onEditDashboard = (data: { name: string; description: string }) => {
    if (selectedDashboard?.id) {
      try {
        updatePageMutation.mutate({ id: selectedDashboard.id, payload: { ...selectedDashboard, ...data } });
      } catch (error) {
        console.error(error);
      }
    }
    setSelectedDashboard(null);
    setIsEditDashboardOpen(false);
  };

  const header = (
    <Group>
      <Icon name="dashboard" size={36} />
      <H2>{i18n.t('navigation.dashboards')}</H2>
      <Button
        variant="primary"
        radius="xl"
        size="sm"
        rightSection={<Icon name="add_circle_outline" color={baseWhite} size={20} />}
        onClick={() => setIsCreateDashboardOpen(true)}
      >
        {i18n.t('common.dashboard.create_button')}
      </Button>
    </Group>
  );

  // Render search results or empty state
  const renderContent = () => {
    if (searchQuery && selectedTab === DashboardTab.DASHBOARDS && filteredDashboards.length === 0) {
      return (
        <EmptyStateBox
          title={`No ${i18n.t('common.dashboard.dashboards')} Found`}
          icon="search"
          description="Try a different search term or clear the search."
          onClick={() => setSearchQuery('')}
        />
      );
    }

    if (searchQuery && selectedTab === DashboardTab.TEMPLATES && filteredTemplates.length === 0) {
      return (
        <EmptyStateBox
          title={`No ${i18n.t('common.dashboard.templates')} Found`}
          icon="search"
          description="Try a different search term or clear the search."
          onClick={() => setSearchQuery('')}
        />
      );
    }

    if (selectedTab === DashboardTab.DASHBOARDS) {
      return (
        <DashboardsList
          pages={filteredDashboards}
          pagesQuery={pagesQuery}
          handleCreateDashboard={() => setIsCreateDashboardOpen(true)}
          handleEditDashboard={handleEditDashboard}
        />
      );
    }

    if (selectedTab === DashboardTab.TEMPLATES) {
      return <DashboardTemplateList pageTemplates={filteredTemplates} pageTemplatesQuery={pageTemplatesQuery} />;
    }

    return null;
  };

  return (
    <MenuDrivenContainer header={header}>
      <Group justify="space-between" align="center">
        <Group mb="md" gap="xs">
          {[DashboardTab.DASHBOARDS, DashboardTab.TEMPLATES].map((tab) => {
            const isSelected = tab === selectedTab;
            return (
              <Button
                size="xs"
                radius="xl"
                key={tab}
                variant={isSelected ? 'secondary' : 'outline'}
                onClick={() => {
                  setSelectedTab(tab);
                  setSearchQuery('');
                }}
                leftSection={
                  <Icon
                    name={tab === DashboardTab.DASHBOARDS ? 'dashboard' : 'dashboard_customize'}
                    color={isSelected ? baseWhite : inkLight}
                    size={16}
                    style={{ cursor: 'pointer' }}
                  />
                }
              >
                {tab === DashboardTab.DASHBOARDS
                  ? i18n.t('common.dashboard.dashboards')
                  : i18n.t('common.dashboard.templates')}
              </Button>
            );
          })}
        </Group>
        <Group>
          <TextInput
            placeholder={`Search ${
              selectedTab === DashboardTab.DASHBOARDS
                ? i18n.t('common.dashboard.dashboards')
                : i18n.t('common.dashboard.templates')
            }`}
            radius="xl"
            size="sm"
            w="300px"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            rightSection={
              searchQuery ? (
                <Icon name="close" size={16} style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')} />
              ) : (
                <Icon name="search" size={16} />
              )
            }
          />
        </Group>
      </Group>
      {renderContent()}
      <EditDashboardModal
        opened={isEditDashboardOpen || isCreateDashboardOpen}
        onClose={() => {
          setIsEditDashboardOpen(false);
          setSelectedDashboard(null);
          setIsCreateDashboardOpen(false);
        }}
        onCreate={onCreateDashboard}
        onEdit={onEditDashboard}
        initialData={{
          name: selectedDashboard?.name || '',
          description: selectedDashboard?.description || '',
        }}
        isCreating={isCreateDashboardOpen}
      />
    </MenuDrivenContainer>
  );
};
